import * as React from 'react';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  History,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';

export interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface AuditEntry {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  action: 'created' | 'updated' | 'deleted' | 'approved' | 'rejected';
  timestamp: string; // ISO-8601
  changes?: FieldChange[];
  comment?: string;
}

export interface AuditTrailProps {
  entries: AuditEntry[];
  isLoading?: boolean;
  maxEntries?: number;
  className?: string;
}

// Get icon and colors for action type
function getActionConfig(action: string): {
  icon: React.ReactNode;
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline';
  bgColor: string;
  dotColor: string;
} {
  const configMap: Record<
    string,
    {
      icon: React.ReactNode;
      badgeVariant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline';
      bgColor: string;
      dotColor: string;
    }
  > = {
    created: {
      icon: <Plus className="h-4 w-4" />,
      badgeVariant: 'success',
      bgColor: 'bg-[var(--color-success)]/10',
      dotColor: 'bg-[var(--color-success)]',
    },
    updated: {
      icon: <Pencil className="h-4 w-4" />,
      badgeVariant: 'default',
      bgColor: 'bg-[var(--color-primary)]/10',
      dotColor: 'bg-[var(--color-primary)]',
    },
    deleted: {
      icon: <Trash2 className="h-4 w-4" />,
      badgeVariant: 'destructive',
      bgColor: 'bg-[var(--color-destructive)]/10',
      dotColor: 'bg-[var(--color-destructive)]',
    },
    approved: {
      icon: <Check className="h-4 w-4" />,
      badgeVariant: 'success',
      bgColor: 'bg-[var(--color-success)]/10',
      dotColor: 'bg-[var(--color-success)]',
    },
    rejected: {
      icon: <X className="h-4 w-4" />,
      badgeVariant: 'destructive',
      bgColor: 'bg-[var(--color-destructive)]/10',
      dotColor: 'bg-[var(--color-destructive)]',
    },
  };

  return (
    configMap[action] || {
      icon: <History className="h-4 w-4" />,
      badgeVariant: 'secondary' as const,
      bgColor: 'bg-[var(--color-muted)]/10',
      dotColor: 'bg-[var(--color-muted)]',
    }
  );
}

// Capitalize action for display
function capitalizeAction(action: string): string {
  return action.charAt(0).toUpperCase() + action.slice(1);
}

// Format timestamp
function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  } catch {
    return timestamp;
  }
}

// Generate initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Get avatar color based on name
function getAvatarColor(name: string): string {
  const colors = [
    'bg-[var(--color-primary)]',
    'bg-[var(--color-success)]',
    'bg-[var(--color-warning)]',
    'bg-[var(--color-destructive)]',
    'bg-[var(--color-secondary)]',
  ];
  const hash = name.charCodeAt(0) + name.charCodeAt(name.length - 1);
  return colors[hash % colors.length];
}

// Truncate long values for display
function truncateValue(value: string | null, maxLength: number = 50): string {
  if (value === null) return '(empty)';
  if (value.length > maxLength) return value.substring(0, maxLength) + '...';
  return value;
}

// Individual audit entry component
interface AuditEntryComponentProps {
  entry: AuditEntry;
  isLast: boolean;
}

const AuditEntryComponent = React.forwardRef<HTMLDivElement, AuditEntryComponentProps>(
  ({ entry, isLast }, ref) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const config = getActionConfig(entry.action);
    const userInitials = getInitials(entry.userName);
    const userAvatarColor = getAvatarColor(entry.userName);
    const hasChanges = entry.changes && entry.changes.length > 0;

    return (
      <div ref={ref} className="flex gap-4">
        {/* Timeline connector */}
        <div className="flex flex-col items-center gap-2">
          {/* Dot */}
          <div
            className={cn(
              'h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-white',
              config.dotColor,
            )}
          >
            {config.icon}
          </div>

          {/* Connecting line */}
          {!isLast && <div className="w-0.5 h-16 bg-[var(--color-border)]" />}
        </div>

        {/* Content */}
        <div className="flex-1 pb-4">
          <div
            className={cn(
              'rounded-lg border border-[var(--color-border)] p-4 transition-colors',
              config.bgColor,
            )}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {/* User avatar */}
                  {entry.userAvatar ? (
                    <img
                      src={entry.userAvatar}
                      alt={entry.userName}
                      className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div
                      className={cn(
                        'h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0',
                        userAvatarColor,
                      )}
                    >
                      {userInitials}
                    </div>
                  )}

                  {/* User name and action */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--color-foreground)]">
                      {entry.userName}
                    </span>
                    <Badge variant={config.badgeVariant} className="text-xs">
                      {capitalizeAction(entry.action)}
                    </Badge>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
                  <Clock className="h-3 w-3" />
                  {formatTimestamp(entry.timestamp)}
                </div>
              </div>

              {/* Expand button */}
              {hasChanges && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-6 w-6 p-0"
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>

            {/* Comment */}
            {entry.comment && (
              <div className="mb-3 text-sm text-[var(--color-foreground)]">
                <p className="italic">{entry.comment}</p>
              </div>
            )}

            {/* Field changes (collapsible) */}
            {hasChanges && isExpanded && (
              <div className="mt-3 space-y-2 border-t border-[var(--color-border)] pt-3">
                {entry.changes!.map((change, idx) => (
                  <div key={`${entry.id}-change-${idx}`} className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-[var(--color-foreground)]">
                      {change.field}
                    </span>
                    <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
                      <span className="font-mono bg-[var(--color-muted)]/30 px-2 py-1 rounded max-w-xs truncate">
                        {truncateValue(change.oldValue)}
                      </span>
                      <span className="flex-shrink-0">→</span>
                      <span className="font-mono bg-[var(--color-success)]/20 px-2 py-1 rounded max-w-xs truncate">
                        {truncateValue(change.newValue)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

AuditEntryComponent.displayName = 'AuditEntryComponent';

// Loading skeleton
function AuditEntrySkeleton(): JSX.Element {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="w-0.5 h-16" />
      </div>
      <div className="flex-1">
        <Skeleton className="h-24 rounded-lg" />
      </div>
    </div>
  );
}

// Main component
export const AuditTrail = React.forwardRef<HTMLDivElement, AuditTrailProps>(
  ({ entries, isLoading = false, maxEntries, className }, ref) => {
    const displayEntries = maxEntries ? entries.slice(0, maxEntries) : entries;

    return (
      <div ref={ref} className={cn('space-y-4', className)}>
        {isLoading ? (
          <>
            <AuditEntrySkeleton />
            <AuditEntrySkeleton />
            <AuditEntrySkeleton />
          </>
        ) : displayEntries.length > 0 ? (
          displayEntries.map((entry, idx) => (
            <AuditEntryComponent
              key={entry.id}
              entry={entry}
              isLast={idx === displayEntries.length - 1}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-8 w-8 mb-3 text-[var(--color-muted-foreground)]" />
            <p className="text-sm font-medium text-[var(--color-foreground)]">
              No audit history
            </p>
            <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
              Changes to this record will appear here
            </p>
          </div>
        )}
      </div>
    );
  },
);

AuditTrail.displayName = 'AuditTrail';
